import { useState, useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addBin, updateBin, getRackList } from 'helpers/backend_helper';
import { Common } from 'helpers/common';

export const useBinModal = (onSave: (result: any) => void, initialData?: any) => {
    const [show, setShow] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [binData, setBinData] = useState<any>(initialData);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [rackList, setRackList] = useState<any[]>([]);
    const commonHelper = new Common();

    useEffect(() => {
        if (initialData) {
            setBinData(initialData);
            setIsEdit(!!initialData.id);
        }
    }, [initialData]);

    useEffect(() => {
        loadRacks();
    }, []);

    const loadRacks = async () => {
        try {
            const response = await getRackList();
            if (response) {
                setRackList(response.data || []);
            }
        } catch (error) {
            console.error('Error loading racks:', error);
        }
    };

    const toggle = useCallback(() => {
        setShow(!show);
        if (show) {
            setBinData(null);
        }
    }, [show]);

    const handleEditDataClick = (ele: any) => {
        setBinData({ ...ele });
        setIsEdit(true);
        setShow(true);
    };

    const handleAddBin = () => {
        setBinData({});
        toggle();
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: binData?.name || "",
            rack_id: binData?.rack_id || ""
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Please enter bin name"),
            rack_id: Yup.string().required("Please select rack")
        }),
        onSubmit: async (values: any) => {
            setIsSaving(true);
            try {
                let result;
                if (isEdit) {
                    result = await updateBin({ ...values, id: binData.id });
                    commonHelper.showSuccessMessage("Bin updated successfully");
                } else {
                    result = await addBin(values);
                    commonHelper.showSuccessMessage("Bin added successfully");
                }
                
                if (result) {
                    onSave(result);
                }
            } catch (error) {
                console.error('Error saving bin:', error);
                commonHelper.showErrorMessage(isEdit ? "Failed to update bin" : "Failed to add bin");
            } finally {
                setIsSaving(false);
            }
        }
    });

    return {
        show,
        isEdit,
        binData,
        toggle,
        handleEditDataClick,
        handleAddBin,
        validation,
        isSaving,
        rackList
    };
};